import React, { useState, useMemo, useRef } from "react";
import { Row, Col, Card, Form, Button, Alert } from "react-bootstrap";
import html2canvas from "html2canvas";
import ShotMapCanvas from "./ShotMapCanvas";
import ShotMapLegend from "./ShotMapLegend";
import { useShotMarkers } from "../hooks/useShotMarkers";
import "./ShotMapWithFilters.css"; // re-use your existing CSS

const sortByPreferredOrder = (values, preferredOrder) => {
  const seen = new Set();
  const out = [];
  preferredOrder.forEach((v) => {
    if (values.includes(v) && !seen.has(v)) {
      out.push(v);
      seen.add(v);
    }
  });
  values.forEach((v) => {
    if (!seen.has(v)) out.push(v);
  });
  return out;
};

const formatShotTypeLabel = (value) =>
  value === "All" ? value : String(value || "").replace(/\s*attempt$/i, "");

export default function SeasonShotMap({ shotsData = [] }) {
  // -- Filters state (no team/player here) --
  const [shotType, setShotType] = useState("All");
  const [setPlay, setSetPlay] = useState("All");
  const [gameHalf, setGameHalf] = useState("All");
  const [shotOutcome, setShotOutcome] = useState("All");
  const [shotPressure, setShotPressure] = useState("All");
  const [shotMethod, setShotMethod] = useState("All");
  const mapExportRef = useRef(null);

  const resetFilters = () => {
    setShotType("All");
    setSetPlay("All");
    setGameHalf("All");
    setShotOutcome("All");
    setShotPressure("All");
    setShotMethod("All");
  };

  // -- Dropdown options --
  const types = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotType))],
    [shotsData],
  );
  const plays = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.setPlay))],
    [shotsData],
  );
  const halves = useMemo(() => ["All", "1", "2", "3"], []);
  const outcomes = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotOutcome))],
    [shotsData],
  );
  const pressures = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotPressure))],
    [shotsData],
  );
  const methods = useMemo(
    () => ["All", ...new Set(shotsData.map((s) => s.shotMethod))],
    [shotsData],
  );
  const orderedTypes = useMemo(
    () =>
      sortByPreferredOrder(types, [
        "All",
        "Goal attempt",
        "Point attempt",
        "2 Point attempt",
      ]),
    [types],
  );
  const orderedPressures = useMemo(
    () => sortByPreferredOrder(pressures, ["All", "Low", "Medium", "High"]),
    [pressures],
  );
  const orderedMethods = useMemo(
    () =>
      sortByPreferredOrder(methods, [
        "All",
        "Hand",
        "Left foot",
        "Right foot",
      ]),
    [methods],
  );

  // -- Apply filters --
  const filtered = useMemo(
    () =>
      shotsData.filter((s) => {
        if (shotType !== "All" && s.shotType !== shotType) return false;
        if (setPlay !== "All" && s.setPlay !== setPlay) return false;
        if (gameHalf !== "All" && String(s.gameHalf) !== gameHalf) return false;
        if (shotOutcome !== "All" && s.shotOutcome !== shotOutcome)
          return false;
        if (shotPressure !== "All" && s.shotPressure !== shotPressure)
          return false;
        if (shotMethod !== "All" && s.shotMethod !== shotMethod) return false;
        return true;
      }),
    [
      shotsData,
      shotType,
      setPlay,
      gameHalf,
      shotOutcome,
      shotPressure,
      shotMethod,
    ],
  );

  const { markers, summary } = useShotMarkers(filtered);

  const handleDownloadMap = async () => {
    if (!mapExportRef.current) return;
    const canvas = await html2canvas(mapExportRef.current, {
      backgroundColor: null,
      scale: 2,
    });
    const link = document.createElement("a");
    link.download = "season-shot-map.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <Card className="mb-4">
      <Card.Header>Shot Map</Card.Header>
      <Card.Body>
        <Row>
          {/* -- FILTERS (no Team/Player) -- */}
          <Col md={3} className="filters">
            <Form.Group className="mb-2">
              <Form.Label>Game Half</Form.Label>
              <div>
                {halves.map((h) => (
                  <Form.Check
                    key={h}
                    inline
                    type="radio"
                    id={`season-map-half-${h}`}
                    name="seasonMapHalf"
                    label={h}
                    value={h}
                    checked={gameHalf === h}
                    onChange={(e) => setGameHalf(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Shot Type</Form.Label>
              <div className="map-radio-grid map-radio-grid-2">
                {orderedTypes.map((t) => (
                  <Form.Check
                    key={t}
                    type="radio"
                    id={`season-map-type-${t}`}
                    name="seasonMapType"
                    label={formatShotTypeLabel(t)}
                    value={t}
                    checked={shotType === t}
                    onChange={(e) => setShotType(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Set Play</Form.Label>
              <Form.Select
                value={setPlay}
                onChange={(e) => setSetPlay(e.target.value)}
              >
                {plays.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Pressure</Form.Label>
              <div className="map-radio-grid map-radio-grid-2">
                {orderedPressures.map((p) => (
                  <Form.Check
                    key={p}
                    type="radio"
                    id={`season-map-pressure-${p}`}
                    name="seasonMapPressure"
                    label={p}
                    value={p}
                    checked={shotPressure === p}
                    onChange={(e) => setShotPressure(e.target.value)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Method</Form.Label>
              <div className="map-radio-grid map-radio-grid-2">
                {orderedMethods.map((m) => (
                  <Form.Check
                    key={m}
                    type="radio"
                    id={`season-map-method-${m}`}
                    name="seasonMapMethod"
                    label={m}
                    value={m}
                    checked={shotMethod === m}
                    onChange={(e) => setShotMethod(e.target.value)}
                  />
                ))}
              </div>

              <Form.Group className="mb-2">
                <Form.Label>Outcome</Form.Label>
                <Form.Select
                  value={shotOutcome}
                  onChange={(e) => setShotOutcome(e.target.value)}
                >
                  {outcomes.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form.Group>
            <Button variant="warning" onClick={resetFilters} className="w-100">
              Reset Filters
            </Button>
            <Button
              variant="outline-light"
              onClick={handleDownloadMap}
              className="w-100 mt-2"
            >
              Download Map
            </Button>
          </Col>

          <Col md={9}>
            <div ref={mapExportRef} className="map-export">
              <Row className="g-0">
                {/* -- PITCH + MARKERS -- */}
                <Col md={9} className="pitch-wrapper">
                  <ShotMapCanvas
                    markers={markers}
                    summaryText={`${summary.total} Shots, xPoints ${summary.xp.toFixed(
                      2,
                    )}, Points ${summary.pts}, Efficiency ${summary.eff}`}
                  />
                  {filtered.length === 0 && (
                    <Alert className="app-empty-state mt-3 mb-0">
                      No shots match these filters.
                    </Alert>
                  )}
                </Col>

                {/* -- LEGEND -- */}
                <Col md={3}>
                  <ShotMapLegend />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}




